import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import {
    clearListScroll,
    offsetFromScroll,
    readListScroll,
    scrollStorageKey,
    writeListScroll,
} from './listViewStorage';

const RESTORE_GUARD_MS = 400;

function resolveOffset(saved, itemSize, columns) {
    if (saved.fetchOffset > 0) {
        return saved.fetchOffset;
    }
    return offsetFromScroll({
        scrollOffset: saved.scrollOffset,
        itemSize,
        columns,
    });
}

function applyListScroll(list, target) {
    if (!list || !target || !list.scrollTo) {
        return false;
    }
    list.scrollTo(target);
    return true;
}

export function useListScrollRestore({
    id,
    pathname = '',
    displayMode = 'default',
    itemSize,
    columns = 1,
    scrollRef,
    setOffset,
    ready = false,
    resetOnFilters,
    resetOnSort,
    enabled = true,
} = {}) {
    const key = scrollStorageKey({ id, pathname, displayMode });
    const skipResetRef = useRef(true);
    const previousKeyRef = useRef(key);
    const setOffsetRef = useRef(setOffset);
    const restoringRef = useRef(true);
    const pendingScrollRef = useRef(enabled ? readListScroll(key).scrollOffset : 0);
    const initialScrollRef = useRef({ key, value: pendingScrollRef.current });
    if (initialScrollRef.current.key !== key) {
        initialScrollRef.current = { key, value: enabled ? readListScroll(key).scrollOffset : 0 };
    }
    const initialScrollOffset = initialScrollRef.current.value;
    setOffsetRef.current = setOffset;

    useLayoutEffect(() => {
        pendingScrollRef.current = enabled ? readListScroll(key).scrollOffset : 0;
        restoringRef.current = pendingScrollRef.current > 0;
    }, [enabled, key]);

    const persistScroll = useCallback((payload) => {
        if (!enabled) {
            return;
        }
        const scrollOffset = typeof payload === 'number' ? payload : payload?.scrollOffset;
        const fetchOffset = typeof payload === 'object' ? payload?.fetchOffset : undefined;
        const nextOffset = scrollOffset ?? 0;
        // The virtual list mounts at 0 and fires onScroll(0) before restore.
        // Do not let that wipe the saved card/row position.
        if (restoringRef.current && nextOffset === 0 && pendingScrollRef.current > 0) {
            return;
        }
        pendingScrollRef.current = nextOffset;
        writeListScroll(key, { scrollOffset: nextOffset, fetchOffset });
    }, [enabled, key]);

    const resetScroll = useCallback(() => {
        restoringRef.current = false;
        pendingScrollRef.current = 0;
        clearListScroll(key);
        setOffsetRef.current?.(0);
        if (scrollRef?.current?.scrollTo) {
            scrollRef.current.scrollTo(0);
        }
    }, [key, scrollRef]);

    useEffect(() => {
        if (!enabled) {
            return;
        }
        if (previousKeyRef.current === key) {
            return;
        }
        previousKeyRef.current = key;
        const saved = readListScroll(key);
        pendingScrollRef.current = saved.scrollOffset;
        restoringRef.current = saved.scrollOffset > 0;
        setOffsetRef.current?.(resolveOffset(saved, itemSize, columns));
    }, [columns, enabled, itemSize, key]);

    const resetSignature = JSON.stringify({ filters: resetOnFilters, sort: resetOnSort });
    const previousSignatureRef = useRef(resetSignature);

    useEffect(() => {
        if (!enabled) {
            return;
        }
        if (skipResetRef.current) {
            skipResetRef.current = false;
            previousSignatureRef.current = resetSignature;
            return;
        }
        if (previousSignatureRef.current === resetSignature) {
            return;
        }
        previousSignatureRef.current = resetSignature;
        resetScroll();
    }, [enabled, resetSignature, resetScroll]);

    useLayoutEffect(() => {
        if (!enabled || !ready) {
            return;
        }
        const target = pendingScrollRef.current;
        if (!target) {
            restoringRef.current = false;
            return;
        }
        restoringRef.current = true;
        applyListScroll(scrollRef?.current, target);
        const raf = requestAnimationFrame(() => {
            applyListScroll(scrollRef?.current, target);
        });
        const done = setTimeout(() => {
            applyListScroll(scrollRef?.current, target);
            restoringRef.current = false;
        }, RESTORE_GUARD_MS);
        return () => {
            cancelAnimationFrame(raf);
            clearTimeout(done);
        };
    }, [enabled, ready, key, scrollRef]);

    return useMemo(
        () => ({ persistScroll, resetScroll, initialScrollOffset }),
        [persistScroll, resetScroll, initialScrollOffset],
    );
}
